/**
 * WoodchuckMascot.js
 * Animated cypherpunk NFT-style woodchuck character for the menu screen.
 * Renders pixel art with layered neon glow effects and idle animations.
 */

// ── Colour palette ──────────────────────────────────────────────────
const PAL = {
  // Fur
  FUR_DARK:   0x3D2517,
  FUR:        0x6B4423,
  FUR_MID:    0x8B5E3C,
  FUR_LIGHT:  0xC4915E,
  BELLY:      0xD4A574,
  EAR_INNER:  0xBB7744,
  // Face
  NOSE:       0x111111,
  TEETH:      0xEEEEDD,
  MOUTH:      0x331111,
  // Cyberpunk neon
  CYAN:       0x00FFFF,
  CYAN_DIM:   0x007788,
  MAGENTA:    0xFF00FF,
  MAG_DIM:    0x880088,
  GREEN:      0x39FF14,
  GREEN_DIM:  0x1A8F0A,
  RED:        0xFF0044,
  // Gear
  VISOR_BG:   0x112233,
  VISOR_EDGE: 0x00AACC,
  VEST:       0x1A0F2E,
  VEST_TRIM:  0x2D1B4E,
  // Base
  BLACK:      0x0A0A0A,
  OUTLINE:    0x110A05,
};

/**
 * 28 × 34 pixel art – cypherpunk woodchuck (front-facing NFT portrait)
 *
 * Legend:
 *   .  transparent       O  outline          d  fur dark
 *   f  fur               m  fur mid          L  fur light / muzzle
 *   B  belly             E  ear inner        K  black
 *   W  teeth white       N  nose             H  mouth
 *   C  cyan neon         c  cyan dim         R  red cyber-eye
 *   M  magenta           g  green circuit    V  visor bg
 *   v  visor edge        P  vest             p  vest trim
 */
const SPRITE_ROWS = [
  //0         1         2
  //0123456789012345678901234567
  '......CC..........CC........', // 0  ear tips
  '.....CddC........CddC......', // 1  ears
  '.....CdEC........CEdC......', // 2  ear inner
  '.....CddCCCCCCCCCCddC......', // 3  head top
  '....CdfffffffffffffffdC....', // 4  forehead
  '...CdfffffffffffffffffffC..', // 5  head
  '..CdffffffffffffffffffffdC.', // 6  head wide
  '..CdffffffffffffffffffffdC.', // 7  head
  '..CdfvvvvvvvvvvvvvvvvffdC.', // 8  visor top edge
  '..CdfVVRKVVVVVVVVKCVVffdC.', // 9  eyes: R=red C=cyan K=pupil
  '..CdfvvvvvvvvvvvvvvvvffdC.', // 10 visor bottom edge
  '..CdffffffffffffffffffffdC.', // 11 under visor
  '..CdffffmmffKKffmmffffdC...', // 12 nose area
  '..CdfffffLLKKKKLLfffffdC...', // 13 muzzle + nose
  '..CdffffLLWWWWWWLLfffdC....', // 14 TEETH row 1!
  '..CdffffLLWWWWWWLLfffdC....', // 15 TEETH row 2!
  '...CdfffLLLHHHHLLLfffdC....', // 16 mouth
  '...CdfffLLLLLLLLLLfffdC....', // 17 chin
  '....CdfffffffffffffddC.....', // 18 lower face
  '....CdffffffffffffdC.......', // 19 neck
  '...CMdfffffffffffffdMC.....', // 20 magenta collar
  '..CPPddddddddddddddPPC....', // 21 vest top
  '..CPdfgggggggggggfdPPC.....', // 22 vest + circuits
  '..CPdfgggggggggggfdPPC.....', // 23 vest + circuits
  '..CPddfffffffffffddPPC.....', // 24 vest body
  '...CPdfBBBBBBBBBfdPC.......', // 25 belly
  '...CPdfBBBBBBBBBfdPC.......', // 26 belly
  '....CPddddddddddPC........', // 27 vest bottom
  '.....Cdd...CdddC...CfC....', // 28 legs + tail start
  '.....Cdd...CdddC..CffdC...', // 29 legs + tail
  '.....Cdd...CdddC.CffffdC..', // 30 legs + tail bushy
  '.....CCC....CCC..CffffdC..', // 31 feet + tail
  '..................CfffdC...', // 32 tail tip
  '...................CCC.....', // 33 tail tip neon
];

const CHAR_TO_COLOR = {
  'O': PAL.OUTLINE,
  'd': PAL.FUR_DARK,
  'f': PAL.FUR_MID,
  'm': PAL.FUR_LIGHT,
  'L': PAL.FUR_LIGHT,
  'B': PAL.BELLY,
  'E': PAL.EAR_INNER,
  'K': PAL.BLACK,
  'W': PAL.TEETH,
  'N': PAL.NOSE,
  'H': PAL.MOUTH,
  'C': PAL.CYAN,
  'c': PAL.CYAN_DIM,
  'R': PAL.RED,
  'M': PAL.MAGENTA,
  'g': PAL.GREEN,
  'V': PAL.VISOR_BG,
  'v': PAL.VISOR_EDGE,
  'P': PAL.VEST,
  'p': PAL.VEST_TRIM,
};

// Characters that are part of the neon glow overlay
const NEON_CHARS = new Set(['C', 'M', 'g', 'v', 'R']);

// Characters that are the eyes (for blink animation)
const EYE_CHARS = new Set(['R', 'K', 'C']);
const EYE_ROWS = [9]; // only row 9 has eyes

// ── Public API ──────────────────────────────────────────────────────

/**
 * Create and return an animated cypherpunk woodchuck on the given scene.
 * @param {Phaser.Scene} scene
 * @param {number} cx   centre-x
 * @param {number} cy   centre-y
 * @param {number} [pixelScale=5]  size of each pixel square
 * @returns {{ container, destroy }}
 */
export function createMascot(scene, cx, cy, pixelScale = 5) {
  const cols = SPRITE_ROWS[0].length;
  const rows = SPRITE_ROWS.length;
  const totalW = cols * pixelScale;
  const totalH = rows * pixelScale;
  const ox = cx - totalW / 2;
  const oy = cy - totalH / 2;

  // ── Layer 1: Glow aura (behind character) ─────────────────────────
  const glowGfx = scene.add.graphics();
  drawGlow(glowGfx, cx, cy, totalW, totalH);

  // ── Layer 2: Main character pixels ────────────────────────────────
  const baseGfx = scene.add.graphics();
  drawBase(baseGfx, ox, oy, pixelScale);

  // ── Layer 3: Neon overlay (outlines + circuits, pulsing) ──────────
  const neonGfx = scene.add.graphics();
  drawNeon(neonGfx, ox, oy, pixelScale);

  // ── Layer 4: Eye layer (for blink animation) ─────────────────────
  const eyeGfx = scene.add.graphics();
  drawEyes(eyeGfx, ox, oy, pixelScale);

  // ── Layer 5: Scanline overlay ────────────────────────────────────
  const scanGfx = scene.add.graphics();
  drawScanlines(scanGfx, ox, oy, totalW, totalH, pixelScale);

  // ── Container ─────────────────────────────────────────────────────
  const container = scene.add.container(0, 0, [
    glowGfx, baseGfx, neonGfx, eyeGfx, scanGfx,
  ]);

  // ── Animations ────────────────────────────────────────────────────

  // 1. Float / bob (gentle sine wave)
  scene.tweens.add({
    targets: container,
    y: { from: -4, to: 4 },
    duration: 2200,
    ease: 'Sine.easeInOut',
    yoyo: true,
    repeat: -1,
  });

  // 2. Glow pulse
  scene.tweens.add({
    targets: glowGfx,
    alpha: { from: 0.25, to: 0.7 },
    duration: 1800,
    ease: 'Sine.easeInOut',
    yoyo: true,
    repeat: -1,
  });

  // 3. Neon line pulse
  scene.tweens.add({
    targets: neonGfx,
    alpha: { from: 0.45, to: 1.0 },
    duration: 1400,
    ease: 'Sine.easeInOut',
    yoyo: true,
    repeat: -1,
  });

  // 4. Eye blink (every 3-6 s)
  const blinkLoop = () => {
    const delay = 3000 + Math.random() * 3000;
    scene.time.delayedCall(delay, () => {
      if (!eyeGfx.scene) return; // scene destroyed check
      eyeGfx.setAlpha(0);
      scene.time.delayedCall(150, () => {
        if (!eyeGfx.scene) return;
        eyeGfx.setAlpha(1);
        blinkLoop();
      });
    });
  };
  blinkLoop();

  // 5. Glitch (occasional horizontal shift)
  const glitchLoop = () => {
    const delay = 4000 + Math.random() * 8000;
    scene.time.delayedCall(delay, () => {
      if (!container.scene) return;
      const shift = (Math.random() > 0.5 ? 1 : -1) * (2 + Math.random() * 3);
      container.x += shift;
      scene.time.delayedCall(80, () => {
        if (!container.scene) return;
        container.x -= shift;
        glitchLoop();
      });
    });
  };
  glitchLoop();

  // 6. Scanline scroll
  scene.tweens.add({
    targets: scanGfx,
    alpha: { from: 0.06, to: 0.15 },
    duration: 3000,
    ease: 'Sine.easeInOut',
    yoyo: true,
    repeat: -1,
  });

  return {
    container,
    destroy() {
      container.destroy(true);
    },
  };
}

// ── Drawing helpers ─────────────────────────────────────────────────

function drawGlow(gfx, cx, cy, w, h) {
  // Outer soft glow (large, dim)
  gfx.fillStyle(PAL.CYAN, 0.08);
  gfx.fillEllipse(cx, cy, w * 1.6, h * 1.4);
  gfx.fillStyle(PAL.MAGENTA, 0.06);
  gfx.fillEllipse(cx + 10, cy + 5, w * 1.3, h * 1.2);
  // Inner brighter glow
  gfx.fillStyle(PAL.CYAN, 0.12);
  gfx.fillEllipse(cx, cy, w * 1.0, h * 0.95);
}

function drawBase(gfx, ox, oy, s) {
  for (let r = 0; r < SPRITE_ROWS.length; r++) {
    const row = SPRITE_ROWS[r];
    for (let c = 0; c < row.length; c++) {
      const ch = row[c];
      if (ch === '.' || NEON_CHARS.has(ch)) continue; // skip empty + neon (drawn separately)
      // Skip eye pixels on eye rows (drawn in eye layer)
      if (EYE_ROWS.includes(r) && EYE_CHARS.has(ch)) continue;
      const color = CHAR_TO_COLOR[ch];
      if (color === undefined) continue;
      gfx.fillStyle(color);
      gfx.fillRect(ox + c * s, oy + r * s, s, s);
    }
  }
}

function drawNeon(gfx, ox, oy, s) {
  for (let r = 0; r < SPRITE_ROWS.length; r++) {
    const row = SPRITE_ROWS[r];
    for (let c = 0; c < row.length; c++) {
      const ch = row[c];
      if (!NEON_CHARS.has(ch)) continue;
      // Skip eyes in the neon layer (they go in the eye layer)
      if (EYE_ROWS.includes(r) && EYE_CHARS.has(ch)) continue;
      const color = CHAR_TO_COLOR[ch];
      if (color === undefined) continue;
      // Draw a slightly larger "bloom" behind each neon pixel
      gfx.fillStyle(color, 0.3);
      gfx.fillRect(ox + c * s - 1, oy + r * s - 1, s + 2, s + 2);
      // Then the solid pixel on top
      gfx.fillStyle(color, 1);
      gfx.fillRect(ox + c * s, oy + r * s, s, s);
    }
  }
}

function drawEyes(gfx, ox, oy, s) {
  for (const r of EYE_ROWS) {
    const row = SPRITE_ROWS[r];
    for (let c = 0; c < row.length; c++) {
      const ch = row[c];
      if (!EYE_CHARS.has(ch)) continue;
      const color = CHAR_TO_COLOR[ch];
      if (color === undefined) continue;
      // Bloom behind eye pixels
      if (ch === 'R' || ch === 'C') {
        gfx.fillStyle(color, 0.4);
        gfx.fillRect(ox + c * s - 2, oy + r * s - 2, s + 4, s + 4);
      }
      gfx.fillStyle(color, 1);
      gfx.fillRect(ox + c * s, oy + r * s, s, s);
    }
  }
}

function drawScanlines(gfx, ox, oy, w, h, s) {
  // Horizontal scanlines across the character for a holographic feel
  gfx.fillStyle(0x00FFFF, 0.1);
  for (let y = 0; y < h; y += s * 2) {
    gfx.fillRect(ox, oy + y, w, 1);
  }
}

const WoodchuckMascot = { createMascot };
export default WoodchuckMascot;
